import { useTranslation } from 'react-i18next';
import {
  PERMISSIONS_MAP_ID, PERMISSIONS_LOWER, COMMISSION_MODEL, WAGE, CALCULATION_MAP,
} from '../utils/enums';
import { usePermissions } from './useCommon';

export function useOptions() {
  const { t } = useTranslation(['common', 'inputs', 'commissions', 'team']);
  const { permissions } = usePermissions();

  const roleOptions = [
    {
      name: t('employee'),
      value: 0,
      id: 'employee',
      tooltipText: t('allowedToManageHisOwn'),
    },
    {
      name: t('manager'),
      value: 1,
      id: 'manager',
      tooltipText: t('allowedToManageAssigned'),
    },
    permissions.isAdmin && {
      name: t('admin'),
      value: 2,
      id: 'admin',
      tooltipText: t('allowedToManageAll'),
    },
  ].filter(Boolean) as Array<{ name: string, value: number, id: string, tooltipText: string }>;

  const paymentTypesOptions = [
    { name: t('hourly', { ns: 'inputs' }), value: 0, id: 'hourly' },
    { name: t('salary', { ns: 'inputs' }), value: 1, id: 'weekly' },
  ];

  const assignTypesOptions = [
    { name: t('selectManually', { ns: 'commissions' }), value: 0, id: 'members' },
    { name: t('allLocationMembers', { ns: 'commissions' }), value: 1, id: 'location' },
  ];

  const calculationTypesOptions = [
    { name: t('totalTeamMembersSales', { ns: 'commissions' }), value: 0, id: 'team' },
    { name: t('totalLocationSales', { ns: 'commissions' }), value: 1, id: 'location' },
  ];

  const commissionModelOptions = [
    { name: t('split', { ns: 'inputs' }), value: 0, id: 'split' },
    { name: t('duplicate', { ns: 'inputs' }), value: 1, id: 'duplicate' },
    { name: t('teamSteps', { ns: 'commissions' }), value: 2, id: 'team steps' },
  ];

  const createBidirectionalMap = <K extends string | number | symbol, V extends string | number | symbol>(map: Record<K, V>) => {
    const reverseMap = Object.entries(map).reduce((acc, [key, value]) => {
      acc[value as unknown as V] = key as unknown as K;
      return acc;
    }, {} as Record<V, K>);

    return {
      toValue: (key: K) => map[key],
      toKey: (value: V) => reverseMap[value],
    };
  };

  const commissionModelMap = createBidirectionalMap(COMMISSION_MODEL);
  const stepsCalculationMap = createBidirectionalMap(CALCULATION_MAP);
  const roleMap = createBidirectionalMap(PERMISSIONS_MAP_ID);
  const wageMap = createBidirectionalMap(WAGE);

  const roleLowerMapNameToId = (name: string) => {
    const { EMPLOYEE, ADMIN, MANAGER } = PERMISSIONS_LOWER;

    const map: { [key: string]: number } = {
      [EMPLOYEE]: 0,
      [MANAGER]: 1,
      [ADMIN]: 2,
    };

    return map[name];
  };

  return {
    roleOptions,
    paymentTypesOptions,
    commissionModelOptions,
    roleMap,
    roleLowerMapNameToId,
    commissionModelMap,
    wageMap,
    assignTypesOptions,
    calculationTypesOptions,
    stepsCalculationMap,
  };
}
